import React, { useState, useEffect } from 'react';
import Loading from "../common/Loading";
import MacAddressInput from "../common/MacAddressInput";
import PropTypes from "prop-types";
import { createAction } from "../../utils/actionUtils";
import { checkMqttCard } from "../../utils/cardUtils";
import { useAddAction } from "../../utils/reactQueryHooks";

export default function ActionModal({closeModal, cards}) {
    const addActionMutation = useAddAction();
    
    const [sensorMacAddress, setSensorMacAddress] = useState("");
    const [macAddress, setMacAddress] = useState("");
    const [actuatorDescription, setActuatorDescription] = useState("");
    const [actuatorType, setActuatorType] = useState("irrigation");
    const [pwm, setPwm] = useState(false);
    const [mqttCards, setMqttCards] = useState([]);

    useEffect(() => {
        const mqtts = cards.filter(card => checkMqttCard(card?.macAddress));
        setMqttCards(mqtts);

        // Set the first MQTT card as default if available
        if(mqtts.length > 0) {
            setSensorMacAddress(mqtts[0].macAddress);
        }
    }, [cards]);

    // Function to handle form submission:
    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (!sensorMacAddress || !macAddress || !actuatorDescription || !actuatorType) {
            alert("Please fill in all fields.");
            return;
        }

        // Create a new action object
        const newAction = createAction(sensorMacAddress, macAddress, actuatorDescription, actuatorType, pwm);
        
        // Use the React Query mutation hook to add the action
        addActionMutation.mutate(newAction, {
            onSuccess: () => {
                closeModal();
            },
            onError: (error) => {
                console.error("Failed to add actuator:", error);
                alert(`Error adding actuator: ${error.message}`);
            }
        });
    };

    if (addActionMutation.isLoading) {
        return <Loading />
    }
    
    return (
        <div className="card-modal">
            {/*We can keep a lot of the same classes from CardModal*/}
            <div className="card-modal-content">
                <h2 className="card-modal-header">Add New Action</h2>
                <form onSubmit={handleSubmit}>
                    {/* For: (select element) */}
                    <div className="card-form-group">
                        <select 
                        id="sensor"
                        name="sensor"
                        onChange={(e) => {
                            setSensorMacAddress(e.target.value);
                        }}
                        value={sensorMacAddress}
                        required
                        >
                            {
                            mqttCards.map((card) => (
                                <option key={card.macAddress} value={card.macAddress}>
                                    {card.sensorDescription || card.macAddress}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/*Actuator Description*/}
                    <div className="card-form-group">
                        <label htmlFor="Description" className="card-modal-label">Description</label>
                        <input 
                            type="text"
                            id="Description"
                            value={actuatorDescription}
                            maxLength="116"
                            placeholder="irrigation A"
                            onChange={(e) => setActuatorDescription(e.target.value)}
                        />
                    </div>

                    {/*MAC Address (actuator)*/}
                    <div className="card-form-group">
                        <label htmlFor="macAddress" className="card-modal-label">MAC Address</label>
                        <MacAddressInput
                            id="macAddress"
                            value={macAddress}
                            onChange={(e) => setMacAddress(e.target.value)}
                            format="XX:XX:XX:XX:XX:XX"
                            pattern="[A-Za-z0-9]{2}(:[A-Za-z0-9]{2}){5}"
                        />
                    </div>

                    {/*Actuator Type (select element) - fixed*/}
                    <div className="card-form-group">
                        <select 
                        id="actuatorType"
                        name="actuatorType"
                        onChange={(e) => {
                            setActuatorType(e.target.value);
                        }}
                        value={actuatorType}
                        >
                            <option value="irrigation">Irrigation</option>
                            <option value="temperature">Temperature</option>
                            <option value="fan">Fan</option>
                            <option value="light">Light</option>
                            <option value="humidity">Humidity</option>
                            <option value="camera">Camera</option>
                        </select>
                    </div>

                    {/*Check box (Actuator or not)*/}
                    <div className="card-form-group">
                        <label htmlFor="pwm" className="card-modal-label">PWM?</label>
                        <input 
                        type="checkbox" 
                        id="pwm"
                        onChange={(e) => setPwm(e.target.checked)}
                        checked={pwm}
                        />
                    </div>

                    <div className="card-form-actions">
                        <button type="submit" className="green-button" style={{marginLeft: "16px"}}>Add Card</button>
                        <button type="button" className="normal-button delete-button" onClick={closeModal}>
                            <i className="ri-close-line custom-close-icon"></i>
                        </button>
                    </div>

                </form>
            </div>
        </div>
    )
}

ActionModal.propTypes = {
    closeModal: PropTypes.func.isRequired,
    cards: PropTypes.arrayOf(PropTypes.shape({
        macAddress: PropTypes.string,
        sensorDescription: PropTypes.string,
        addIrrigation: PropTypes.string,
        humidityHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        humidityLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        temperatureLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        waterLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        cardImage: PropTypes.oneOfType([PropTypes.string]),
        mostRecentBattery: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentTemp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentHumidity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), 
        mostRecentTimestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        mostRecentWater: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightLow: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        lightHigh: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }))
}